import { ArrowRight, ArrowUpRight, BookOpen, ChevronDown, FileDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { GistlyMark } from "../components/GistlyMark";
import { useAuth } from "../lib/auth";
import { useScrollReveal } from "../lib/useScrollReveal";

const navLinks = [
  { href: "#features", label: "ฟีเจอร์" },
  { href: "#how", label: "วิธีใช้งาน" },
  { href: "#pricing", label: "ราคา" }
];

const features = [
  {
    title: "ลิงก์เดียวก็พอ",
    body: "วาง URL ของคลิป YouTube แล้วปล่อยให้ Gistly ถอดเสียง จับประเด็น และเรียบเรียงออกมาเป็นบทสรุปที่มีหัวข้อชัดเจน"
  },
  {
    title: "ไฟล์ของคุณเองก็ได้",
    body: "อัปโหลดไฟล์เสียงหรือวิดีโอได้หลายไฟล์ในครั้งเดียว แล้วให้ระบบรวบรวมใจความสำคัญไว้ในบทสรุปเดียว"
  },
  {
    title: "อ่านเป็นเอกสารจริง",
    body: "บทสรุปมีหัวข้อ สารบัญ และประเด็นย่อย พร้อมดาวน์โหลดเป็น PDF ไปอ่านต่อหรือส่งต่อให้เพื่อน"
  },
  {
    title: "คลังความรู้ส่วนตัว",
    body: "บทสรุปทุกชิ้นเก็บอยู่ในคลังของคุณคนเดียว ค้นด้วยคำไหนก็เจอ กลับมาอ่านซ้ำได้ตลอด"
  },
  {
    title: "สั่งงานผ่าน LINE",
    body: "ส่งลิงก์เข้าไปใน LINE แล้วเดินไปทำอย่างอื่นต่อ บทสรุปจะถูกส่งกลับมาให้เมื่อทำงานเสร็จ"
  },
  {
    title: "เห็นต้นทุนทุกบรรทัด",
    body: "หน้าค่าใช้จ่ายแสดงจำนวน token และต้นทุนจริงของทุกงาน ไม่มีตัวเลขไหนถูกซ่อนไว้"
  }
];

const steps = [
  {
    title: "ส่งสิ่งที่อยากรู้",
    body: "วางลิงก์ อัปโหลดไฟล์ หรือส่งเข้า LINE เลือกทางที่สะดวกที่สุดในตอนนั้น"
  },
  {
    title: "ปล่อยให้ Gistly ทำงาน",
    body: "ระบบถอดเสียง จับประเด็นหลัก แล้วเรียบเรียงเป็นบทสรุปที่มีโครงสร้าง ระหว่างนั้นจะปิดหน้าจอไปก่อนก็ได้"
  },
  {
    title: "อ่านและเก็บไว้",
    body: "เปิดบทสรุปในคลัง ไล่อ่านตามหัวข้อ แชร์เป็นลิงก์ หรือดาวน์โหลดเป็น PDF เก็บไว้"
  }
];

const faqs = [
  {
    q: "ใช้ฟรีได้เท่าไหร่",
    a: "ฟรี 10 บทสรุปต่อเดือน โควตารีเซ็ตทุกต้นเดือน และไม่ต้องผูกบัตรเครดิตตั้งแต่แรก"
  },
  {
    q: "ต้องสมัครสมาชิกไหม",
    a: "ไม่ต้องตั้งรหัสผ่านใหม่ เข้าสู่ระบบด้วยบัญชี LINE ที่คุณใช้อยู่แล้วได้เลย"
  },
  {
    q: "รองรับไฟล์แบบไหนบ้าง",
    a: "รองรับลิงก์ YouTube รวมถึงไฟล์เสียงและวิดีโอที่อัปโหลดผ่านหน้าเว็บ คุณสามารถเพิ่มหลายลิงก์หรือหลายไฟล์ในบทสรุปเดียวได้"
  },
  {
    q: "คนอื่นเห็นบทสรุปของเราไหม",
    a: "ไม่เห็น คลังความรู้ผูกกับบัญชีของคุณคนเดียว บทสรุปที่สร้างขึ้นจะอยู่ในคลังของคุณเท่านั้น"
  }
];

export function LandingPage() {
  const { login } = useAuth();
  const pageRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useScrollReveal(pageRef);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="mk" ref={pageRef}>
      <header className={`mk-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="mk-shell mk-header-inner">
          <a className="mk-logo" href="#top">
            <span className="mk-logo-mark" aria-hidden="true">
              <GistlyMark size={22} page="#141413" facing="#b15b3c" spine="#d4d0c4" />
            </span>
            <span className="mk-logo-word">Gistly</span>
          </a>
          <nav className="mk-nav" aria-label="เมนูหลัก">
            {navLinks.map((link) => (
              <a key={link.href} className="mk-nav-link" href={link.href}>
                {link.label}
              </a>
            ))}
            <button type="button" className="mk-btn mk-btn-primary mk-btn-sm" onClick={login}>
              เริ่มใช้ฟรี
            </button>
          </nav>
        </div>
      </header>

      <main id="top">
        <section className="mk-section mk-hero">
          <div className="mk-shell mk-measure">
            <p className="mk-overline" data-reveal>
              Get the gist
            </p>
            <h1 className="mk-hero-title" data-reveal data-reveal-delay="1">
              คลิปสองชั่วโมง เหลือแค่<em className="mk-accent-terracotta">แก่น</em>ที่คุณต้องรู้
            </h1>
            <p className="mk-lede" data-reveal data-reveal-delay="2">
              วางลิงก์ YouTube หรืออัปโหลดไฟล์ที่คุณอัดเอง แล้ว Gistly จะย่อยให้เหลือบทสรุปที่อ่านจบใน 1 นาที
              พร้อมสารบัญและ PDF ที่ส่งต่อได้
            </p>
            <div className="mk-cta-row" data-reveal data-reveal-delay="3">
              <button type="button" className="mk-btn mk-btn-primary" onClick={login}>
                เริ่มใช้ฟรีด้วย LINE
                <ArrowRight size={16} />
              </button>
              <a className="mk-btn mk-btn-outline" href="#how">
                ดูวิธีใช้งาน
              </a>
            </div>
            <p className="mk-fineprint" data-reveal data-reveal-delay="4">
              ฟรี 10 บทสรุปต่อเดือน · ไม่ต้องใช้บัตรเครดิต
            </p>
          </div>

          <div className="mk-shell">
            <div className="mk-frame" data-reveal data-reveal-delay="4">
              <div className="mk-frame-col mk-frame-source">
                <p className="mk-overline">Source</p>
                <p className="mk-frame-url">youtube.com/watch?v=lecture-08</p>
                <p className="mk-frame-runtime">2:14:08</p>
              </div>
              <div className="mk-frame-col mk-frame-gist">
                <p className="mk-overline">Gist</p>
                <ul className="mk-frame-points">
                  <li>สามเงื่อนไขที่ทำให้โมเดลล้มเหลวในการใช้งานจริง</li>
                  <li>วิธีวัดผลที่ผู้พูดใช้ และเหตุผลที่ทิ้งตัวชี้วัดเดิม</li>
                  <li>ขั้นตอนที่แนะนำให้ทำก่อนปล่อยระบบขึ้นโปรดักชัน</li>
                </ul>
                <div className="mk-frame-modes">
                  <span>
                    <BookOpen size={14} /> อ่าน 1 นาที
                  </span>
                  <span>
                    <FileDown size={14} /> PDF พร้อมโหลด
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mk-shell">
            <dl className="mk-stats" data-reveal>
              <div className="mk-stat">
                <dt>เวลาอ่านเฉลี่ยต่อบทสรุป</dt>
                <dd>1 นาที</dd>
              </div>
              <div className="mk-stat">
                <dt>รูปแบบที่ได้กลับไป</dt>
                <dd>อ่าน · PDF</dd>
              </div>
              <div className="mk-stat">
                <dt>โควตาฟรีทุกเดือน</dt>
                <dd>10 บทสรุป</dd>
              </div>
            </dl>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section" id="features">
          <div className="mk-shell mk-measure">
            <p className="mk-overline" data-reveal>
              Features
            </p>
            <h2 className="mk-section-title" data-reveal data-reveal-delay="1">
              เครื่องมือเท่าที่<em className="mk-accent-olive">จำเป็น</em> ไม่มีอะไรเกินนั้น
            </h2>
            <p className="mk-lede" data-reveal data-reveal-delay="2">
              Gistly ทำอย่างเดียวคือเปลี่ยนของยาวให้เป็นของที่เข้าใจได้เร็ว ทุกอย่างในนี้มีอยู่เพื่อสิ่งนั้น
            </p>
          </div>

          <div className="mk-shell">
            <div className="mk-cards">
              {features.map((feature, index) => (
                <article
                  className="mk-card"
                  key={feature.title}
                  data-reveal
                  data-reveal-delay={index % 2}
                >
                  <span className="mk-card-number">{String(index + 1).padStart(2, "0")}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section" id="how">
          <div className="mk-shell mk-measure">
            <p className="mk-overline" data-reveal>
              How it works
            </p>
            <h2 className="mk-section-title" data-reveal data-reveal-delay="1">
              สามขั้นตอน แล้วจบ
            </h2>
          </div>

          <div className="mk-shell mk-measure-wide">
            <ol className="mk-steps">
              {steps.map((step, index) => (
                <li className="mk-step" key={step.title} data-reveal data-reveal-delay={index}>
                  <span className="mk-step-number">{String(index + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section mk-quote-section">
          <div className="mk-shell mk-measure">
            <blockquote className="mk-quote" data-reveal>
              ความรู้ดี ๆ ไม่ควรต้องแลกด้วยเวลา<em className="mk-accent-blue">สองชั่วโมง</em>
            </blockquote>
            <p className="mk-quote-source" data-reveal data-reveal-delay="1">
              แนวคิดตั้งต้นของ Gistly
            </p>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section" id="pricing">
          <div className="mk-shell mk-measure">
            <p className="mk-overline" data-reveal>
              Pricing
            </p>
            <h2 className="mk-section-title" data-reveal data-reveal-delay="1">
              เริ่ม<em className="mk-accent-terracotta">ฟรี</em> ไม่มีเงื่อนไขซ่อน
            </h2>
          </div>

          <div className="mk-shell mk-measure-wide">
            <div className="mk-plan" data-reveal data-reveal-delay="2">
              <div className="mk-plan-head">
                <p className="mk-overline">Free</p>
                <p className="mk-plan-price">
                  <span className="mk-plan-amount">
                    <span className="mk-plan-currency">฿</span>0
                  </span>
                  <span className="mk-plan-period">/ เดือน</span>
                </p>
                <p className="mk-plan-note">10 บทสรุปต่อเดือน · รีเซ็ตทุกต้นเดือน</p>
              </div>
              <ul className="mk-plan-list">
                <li>ใช้ได้ทุกฟีเจอร์ ไม่มีการล็อกไว้ในแพลนอื่น</li>
                <li>ดาวน์โหลด PDF และแชร์เป็นลิงก์ได้</li>
                <li>คลังความรู้ส่วนตัวที่ค้นหาได้</li>
                <li>เข้าสู่ระบบด้วย LINE ไม่ต้องตั้งรหัสผ่าน</li>
              </ul>
              <button type="button" className="mk-btn mk-btn-primary" onClick={login}>
                เริ่มใช้ฟรีด้วย LINE
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section">
          <div className="mk-shell mk-measure">
            <p className="mk-overline" data-reveal>
              FAQ
            </p>
            <h2 className="mk-section-title" data-reveal data-reveal-delay="1">
              คำถามที่เจอบ่อย
            </h2>
          </div>

          <div className="mk-shell mk-measure-wide">
            <div className="mk-faq" data-reveal>
              {faqs.map((faq) => (
                <details className="mk-faq-item" key={faq.q}>
                  <summary>
                    <span>{faq.q}</span>
                    <ChevronDown size={17} aria-hidden="true" />
                  </summary>
                  <p>{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <hr className="mk-rule" />

        <section className="mk-section mk-closing">
          <div className="mk-shell mk-measure">
            <h2 className="mk-closing-title" data-reveal>
              คลิปที่ดองไว้ รอคุณอยู่
            </h2>
            <p className="mk-lede" data-reveal data-reveal-delay="1">
              เริ่มจากคลิปเดียวที่ตั้งใจจะดูมานาน แล้วดูว่าเหลือแก่นอะไรบ้าง
            </p>
            <div className="mk-cta-row" data-reveal data-reveal-delay="2">
              <button type="button" className="mk-btn mk-btn-primary" onClick={login}>
                เริ่มใช้ฟรีด้วย LINE
                <ArrowRight size={16} />
              </button>
              <a className="mk-btn mk-btn-outline" href="#features">
                ดูฟีเจอร์ทั้งหมด
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="mk-footer">
        <div className="mk-shell mk-footer-inner">
          <div className="mk-footer-brand">
            <span className="mk-logo-mark" aria-hidden="true">
              <GistlyMark size={20} page="#141413" facing="#b15b3c" spine="#d4d0c4" />
            </span>
            <div>
              <p className="mk-logo-word">Gistly</p>
              <p className="mk-footer-tagline">Get the gist คลิปยาวแค่ไหน ก็เหลือแค่แก่น</p>
            </div>
          </div>
          <nav className="mk-footer-nav" aria-label="ลิงก์ท้ายหน้า">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href}>
                {link.label}
              </a>
            ))}
            <button type="button" className="mk-footer-login" onClick={login}>
              เข้าสู่ระบบ
              <ArrowUpRight size={14} />
            </button>
          </nav>
        </div>
        <div className="mk-shell mk-footer-legal">
          <span>© {new Date().getFullYear()} Gistly</span>
          <span>สร้างบทสรุปด้วย Gemini</span>
        </div>
      </footer>
    </div>
  );
}
